import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useAppToast from 'components/toast';
import socketService from 'services/socketService';
import { metaWhatsappApi } from 'api/metaWhatsappApi';
import { noteTyping } from './useTyping';

/**
 * Live inbox updates.
 *
 * ★ THE CLIENT NEVER NAMES A ROOM.
 * `wa:v2:subscribe` carries no arguments — the server resolves which channels
 * this user may see and joins exactly those. Sending a room name would be
 * ignored anyway; the point is that the client cannot widen its own access.
 *
 * ★ EVENTS ARE ENVELOPES.
 * `wa:v2:message` carries identifiers only, never content. Receiving one
 * invalidates the relevant RTK Query tags and the content is refetched over the
 * authorised read path — which is what keeps a room member whose lead scope
 * excludes a conversation from seeing it. Rendering socket payloads directly
 * would bypass that.
 *
 * ★ REVOCATION DEGRADES, IT DOES NOT CRASH.
 * The server evicts without disconnecting and sends `wa:v2:access_revoked`. The
 * lists are invalidated so the interface simply stops showing what is no longer
 * permitted.
 */
/**
 * The server never acknowledges these; see the note at the subscribe call.
 */
const noAck = () => {};

export default function useMetaWhatsappSocket({ enabled = true } = {}) {
	const dispatch = useDispatch();
	const notify = useAppToast();

	useEffect(() => {
		if (!enabled) return undefined;

		const invalidate = (tags) => dispatch(metaWhatsappApi.util.invalidateTags(tags));

		// A new message: refresh the thread it belongs to and the list ordering.
		const onMessage = ({ conversationId }) => {
			invalidate([
				{ type: 'Message', id: conversationId },
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
			]);
		};

		// A delivery tick. Only the thread's messages change.
		const onStatus = ({ conversationId }) => {
			invalidate([{ type: 'Message', id: conversationId }]);
		};

		// Preview, unread count or ordering moved.
		const onConversation = ({ conversationId }) => {
			invalidate([
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
			]);
		};

		// A number's health or blockers changed — the composer and settings both
		// read from this.
		const onChannelState = () => invalidate(['Channel', 'Settings']);

		// Ownership moved. The detail refetch is what actually re-renders the
		// bar and opens or closes the composer; the list refetch keeps the inbox
		// rows honest for people who do not have the thread open.
		const onOwnership = ({ conversationId }) => {
			invalidate([
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: `candidates-${conversationId}` },
				{ type: 'Conversation', id: 'LIST' },
			]);
		};

		const onAccessRevoked = ({ reason, conversationId }) => {
			// Told, not surprised. The user keeps their connection; the interface
			// stops offering what they may no longer do.
			//
			// ★ THE THREAD THEY JUST LOST IS THE ONE THING THAT MUST REFETCH.
			// Invalidating the channel and the list left the OPEN conversation
			// showing its cached content — the server had evicted them from the
			// room and would refuse the next read, but nothing asked it, so the
			// messages stayed on screen until they navigated. The refetch is what
			// turns the eviction into the 403 the view already knows how to draw.
			invalidate([
				'Channel',
				{ type: 'Conversation', id: 'LIST' },
				...(conversationId
					? [
						{ type: 'Conversation', id: conversationId },
						{ type: 'Message', id: conversationId },
					]
					: []),
			]);
			if (reason && reason !== 'SWEEP') {
				notify.info('WhatsApp access changed', 'Your view of this conversation was updated.');
			}
		};

		const onChannelUnavailable = () => {
			invalidate(['Channel', 'Settings', { type: 'Conversation', id: 'LIST' }]);
			notify.warning('Number unavailable', 'A WhatsApp number is no longer available.');
		};

		// A campaign moved state, or its funnel did.
		//
		// ★ TENANT-WIDE, AND AN ENVELOPE LIKE THE REST. The room is per tenant
		// rather than per channel, because a campaign is not attached to one
		// number — it picks one at send time — and the people watching its
		// dashboard are often assigned to none of them. Nothing in the payload is
		// rendered: the screens refetch over the guarded read path, so a listener
		// without `campaign.view` learns nothing from the event itself.
		const onCampaign = ({ campaignId } = {}) => {
			if (!campaignId) return;
			invalidate([
				{ type: 'Campaign', id: campaignId },
				{ type: 'Campaign', id: `stats-${campaignId}` },
				{ type: 'Campaign', id: `recipients-${campaignId}` },
				{ type: 'Campaign', id: 'LIST' },
			]);
		};

		// Another agent typing in a thread this user has open. Identifiers only:
		// the name is resolved locally, nothing from the payload is rendered.
		const onTyping = ({ conversationId, userId } = {}) => noteTyping({ conversationId, userId });

		socketService.on('wa:v2:typing', onTyping);
		socketService.on('wa:v2:message', onMessage);
		socketService.on('wa:v2:status', onStatus);
		socketService.on('wa:v2:conversation', onConversation);
		socketService.on('wa:v2:channel_state', onChannelState);
		socketService.on('wa:v2:ownership', onOwnership);
		socketService.on('wa:v2:access_revoked', onAccessRevoked);
		socketService.on('wa:v2:channel_unavailable', onChannelUnavailable);
		socketService.on('wa:v2:campaign', onCampaign);

		// No room name. The server decides.
		//
		// ★ THE NO-OP ACK IS NOT DECORATION. `socketService.emit` treats a
		// missing third argument as "wrap this in a promise" and hands socket.io
		// an ack callback; these server handlers never acknowledge, so each one
		// is retained for the life of the socket. The hook re-subscribes on every
		// reconnect, so they accumulate. `useTyping.js` documents the same trap.
		socketService.emit('wa:v2:subscribe', undefined, noAck);

		// A reconnect loses room membership, so subscribing again is how it comes
		// back — and it doubles as drift correction, since the server re-resolves.
		const onReconnect = () => socketService.emit('wa:v2:subscribe', undefined, noAck);
		socketService.on('connect', onReconnect);

		return () => {
			socketService.off('wa:v2:typing', onTyping);
			socketService.off('wa:v2:message', onMessage);
			socketService.off('wa:v2:status', onStatus);
			socketService.off('wa:v2:conversation', onConversation);
			socketService.off('wa:v2:channel_state', onChannelState);
			socketService.off('wa:v2:ownership', onOwnership);
			socketService.off('wa:v2:access_revoked', onAccessRevoked);
			socketService.off('wa:v2:channel_unavailable', onChannelUnavailable);
			socketService.off('wa:v2:campaign', onCampaign);
			socketService.off('connect', onReconnect);
		};
	}, [enabled, dispatch, notify]);
}

/**
 * Join one conversation's room while it is open.
 *
 * Separate from the channel subscription because it is scoped to what is on
 * screen: typing indicators and ticks only matter for the thread being read.
 */
export function useMetaWhatsappConversation(conversationId) {
	useEffect(() => {
		if (!conversationId) return undefined;
		// The server re-resolves on every open — room membership is a cache, not
		// a decision.
		socketService.emit('wa:v2:open', { conversationId }, noAck);
		return undefined;
	}, [conversationId]);
}
